import { Controller } from 'stimulus'
import './button_group_component.scss'

export default class extends Controller {
  static classes = ['active']
  static values = {
    multiple: Boolean,
    slideActive: Boolean,
  }
  static targets = [
    'btn',
    'buttonGroupContainer',
    'revealableContents',
    'sharedRevealableContent',
    'sharedRevealableSlot',
    'slideGhostTemplate',
  ]

  connect() {
    const visible = this.isVisible()
    if (visible) {
      this.activateCheckedButtons()
    } else {
      this._visibilityObserver = new ResizeObserver((entries) => {
        const { width, height } = entries[0].contentRect

        if (width > 0 || height > 0) {
          this._visibilityObserver.disconnect()
          this._visibilityObserver = null
          setTimeout(() => this.activateCheckedButtons(), 50)
        }
      })
      this._visibilityObserver.observe(this.element)
    }

    // If inside revealable container then add for-btn-group class so we can decrease the left right padding - CSS on revelable_content_container_component.scss
    const revelableContainerEl = this.buttonGroupContainerTarget.closest(
      '.revealable-content-container__box'
    )
    if (revelableContainerEl)
      revelableContainerEl.classList.add('for-btn-group')
  }

  disconnect() {
    this._visibilityObserver?.disconnect()
    this._alignmentObserver?.disconnect()
    this.finishSlide()
  }

  isVisible() {
    return this.element.offsetParent !== null
  }

  activateCheckedButtons() {
    // Sync now, so a server-rendered active button keeps its look (deactivating everything and
    // re-activating a moment later blinked the selected option on every builder re-render). Then
    // sync once more shortly after, in case the browser restores a different checked radio.
    this.syncActiveToChecked({ all: true })
    setTimeout(() => this.syncActiveToChecked(), 5)
    const foundActive = this.btnTargets.some((btn) =>
      btn.querySelector('input:checked')
    )

    if (!foundActive) {
      this.btnTargets.forEach((target) => {
        if (target.dataset.hasRevealable == 'true') {
          this.updateSharedRevealables(target)

          // Update the alignment of the revealable if it's styled (like an arrow-pointing box).
          if (this.hasStyledRevealables()) {
            const revealable = document.getElementById(
              target.dataset.buttonValue
            )

            revealable.style.display = 'none'
          }
        }
      })
    }
  }

  // We can be called with targe = LABEL, INPUT, or SPAN ...
  // Ignore all by target = INPUT
  // e.currentTarget will always be the LABEL element
  onBtnClick(e) {
    if (e.target.tagName != 'INPUT') {
      // Clicking a label also dispatches a synthetic click on its input, so a button
      // wrapped in a dropdown trigger would see two bubbling clicks and toggle the
      // menu open and shut again. Swallow the original; the input click follows.
      if (e.currentTarget.dataset.hasDropdown === 'true') e.stopPropagation()
      return
    }

    if (this.multipleValue) {
      this.toggleOption(e.currentTarget)
    } else {
      const target = e.currentTarget
      this.withSlideTransition(target, () => {
        this.deactivateAllTargets()
        this.activateTarget(target)
      })
    }
  }

  // Slides the active pill from the previously active button to the newly clicked one.
  // A throwaway ghost element (styled from the live pill's computed style) animates
  // between the two positions while the real target's pill visuals are suppressed,
  // so only one tiny element animates — no layout work, no document snapshots.
  withSlideTransition(target, apply) {
    const previous = this.btnTargets.find((btn) =>
      btn.classList.contains(this.activeClasses[0])
    )
    const skip =
      !this.slideActiveValue ||
      !previous ||
      previous === target ||
      !this.hasSlideGhostTemplateTarget ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (skip) return apply()

    this.finishSlide()

    const container = this.buttonGroupContainerTarget
    const containerRect = container.getBoundingClientRect()
    const from = previous.getBoundingClientRect()
    const to = target.getBoundingClientRect()
    const pillStyle = getComputedStyle(previous)

    const ghost =
      this.slideGhostTemplateTarget.content.firstElementChild.cloneNode(true)
    Object.assign(ghost.style, {
      background: pillStyle.backgroundColor,
      borderRadius: pillStyle.borderRadius,
      boxShadow: pillStyle.boxShadow,
      width: `${from.width}px`,
      height: `${from.height}px`,
      transform: `translate(${from.left - containerRect.left}px, ${
        from.top - containerRect.top
      }px)`,
    })
    container.prepend(ghost)

    apply()
    target.style.backgroundColor = 'transparent'
    target.style.boxShadow = 'none'

    const animation = ghost.animate(
      [
        { transform: ghost.style.transform, width: ghost.style.width },
        {
          transform: `translate(${to.left - containerRect.left}px, ${
            to.top - containerRect.top
          }px)`,
          width: `${to.width}px`,
        },
      ],
      {
        duration: 150,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards',
      }
    )
    this._slide = { ghost, target, animation }
    animation.onfinish = () => this.finishSlide()
  }

  finishSlide() {
    if (!this._slide) return

    this._slide.animation.cancel()
    this._slide.ghost.remove()
    this._slide.target.style.backgroundColor = ''
    this._slide.target.style.boxShadow = ''
    this._slide = null
  }

  toggleOption(target) {
    const input = target.getElementsByTagName('INPUT')[0]
    if (input.checked) {
      this.activateTarget(target)
    } else {
      target.classList.remove(...this.activeClasses)
    }
  }

  // Never removes the active look from a checked button, so it can't blink. `all` (re)activates
  // every checked button, which also sets up its revealable; otherwise only mismatches change.
  syncActiveToChecked({ all = false } = {}) {
    const activeClass = this.activeClasses[0]
    this.btnTargets.forEach((btn) => {
      const checked = !!btn.querySelector('input:checked')
      const active = btn.classList.contains(activeClass)
      if (checked && (all || !active)) this.activateTarget(btn)
      if (!checked && active) btn.classList.remove(...this.activeClasses)
    })
  }

  deactivateAllTargets() {
    this.btnTargets.forEach((btn) => {
      btn.classList.remove(...this.activeClasses)
    })
  }

  activateTarget(target) {
    target.classList.add(...this.activeClasses)
    if (target.dataset.hasRevealable == 'true') {
      this.updateSharedRevealables(target)

      // Update the alignment of the revealable if it's styled (like an arrow-pointing box).
      if (this.hasStyledRevealables()) {
        const revealable = document
          .getElementById(target.dataset.buttonValue)
          .querySelector('.revealable-content-container__box')

        this.observeAndAlign(target, revealable)
      }
    }

    const event = new CustomEvent('button_group:activated', {
      detail: { button: target },
    })
    window.document.dispatchEvent(event)
  }

  // Styled revealables (the arrow-pointing box) need aligning to their button. The :list style
  // renders its revealables inline, unstyled, and has no revealableContents target.
  hasStyledRevealables() {
    return (
      this.hasRevealableContentsTarget &&
      !this.revealableContentsTarget.querySelector(
        '.revealable-content-container--unstyled'
      )
    )
  }

  // ResizeObserver fires when the revealable transitions from hidden (0×0)
  // to visible, and again on any later layout shift (slideDown, font loads,
  // dynamic content). Each fire re-aligns box + arrow.
  observeAndAlign(target, revealable) {
    this._alignmentObserver?.disconnect()
    this._alignmentObserver = new ResizeObserver(() =>
      this.alignBoxAndArrow(target, revealable)
    )
    this._alignmentObserver.observe(revealable)
  }

  alignBoxAndArrow(target, revealable) {
    if (revealable.getBoundingClientRect().width === 0) return
    const firstBtn = this.btnTargets[0]
    if (!firstBtn) return

    revealable.style.marginLeft = ''
    const boxRect = revealable.getBoundingClientRect()
    const firstBtnLeft = firstBtn.getBoundingClientRect().left
    const targetRect = target.getBoundingClientRect()
    const arrow = document
      .getElementById(target.dataset.buttonValue)
      .querySelector('.revealable-content-container__arrow')

    // Align the box's left edge with the leftmost button, but if the active button
    // extends past the box's right edge, shift the box right so it still covers the
    // active button — otherwise the arrow (which points at the button's center) ends
    // up floating outside the box. Caps at firstBtnLeft so narrow boxes don't drift.
    const boxLeft = Math.max(firstBtnLeft, targetRect.right - boxRect.width)
    const offset = boxLeft - boxRect.left
    if (offset !== 0) revealable.style.marginLeft = offset + 'px'
    // The arrow's left in box-local coords is the active button's center relative
    // to the (possibly shifted) box left.
    arrow.style.left =
      targetRect.left +
      targetRect.width / 2 -
      boxLeft -
      arrow.offsetWidth / 2 +
      'px'
  }

  // See examples of shared revealables in https://us.simplero.test/super_admin/playground/view_component
  updateSharedRevealables(activeTarget) {
    if (!this.hasSharedRevealableContentTarget) return

    const activeRevealableContainer = document.getElementById(
      activeTarget.dataset.buttonValue
    )

    this.sharedRevealableContentTargets.map((content) => {
      const activeSlot = this.sharedRevealableSlotTargets.find(
        (slot) =>
          slot.dataset.sharedRevealableId ===
            content.dataset.sharedRevealableId &&
          activeRevealableContainer.contains(slot)
      )
      activeSlot?.appendChild(content)
    })
  }
}
